import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { captureConnectionsEvent } from '../lib/captureConnectionsEvent';
import { ensureHomeWorkspace } from '../lib/workspaceActions';
import {
  getWorkspaceDetailPath,
  resolveMostRecentWorkspace,
} from '../lib/resolveMostRecentWorkspace';
import { resetWorkspaceState } from '../store/workspacesSlice';
import { persistor } from '../store';
import OnboardingTour from './OnboardingTour/OnboardingTour';

/**
 * Invisible /workspace gate: resume latest workspace or create "My workspace".
 * Replaces the retired workspace picker (WorkspaceView).
 */
const HomeGate = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [showOnboardingTour, setShowOnboardingTour] = useState(false);
  const [phase, setPhase] = useState('loading'); // loading | tour | error
  const [retryKey, setRetryKey] = useState(0);
  const legacyPickerFiredRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();
      setUser(authUser);
      if (!authUser) {
        setPhase('error');
      }
    };
    fetchUser();
  }, []);

  const navigateHome = useCallback(
    async (authUser) => {
      const { workspace, created } = await ensureHomeWorkspace({
        userId: authUser.id,
      });

      if (created) {
        const reason =
          location.state?.reason === 'last_deleted'
            ? 'last_deleted'
            : 'no_workspaces';
        captureConnectionsEvent('home_workspace_auto_created', { reason });
      }

      if (!isMountedRef.current) return;

      navigate(getWorkspaceDetailPath(workspace), {
        replace: true,
        state: {
          workspaceName: workspace.name,
          workspaceOpenSource: created ? 'created' : 'default_resume',
        },
      });
    },
    [location.state?.reason, navigate]
  );

  useEffect(() => {
    if (!user) return undefined;

    let cancelled = false;

    const resolveHome = async () => {
      setPhase('loading');
      setShowOnboardingTour(false);

      try {
        if (
          searchParams.get('picker') === '1' &&
          !legacyPickerFiredRef.current
        ) {
          legacyPickerFiredRef.current = true;
          const mostRecent = await resolveMostRecentWorkspace(user.id);
          captureConnectionsEvent('legacy_picker_url_opened', {
            has_workspaces: Boolean(mostRecent),
          });
        }

        if (cancelled) return;

        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('onboarding_tour_completed')
          .eq('id', user.id)
          .single();

        if (profileError) throw profileError;
        if (cancelled) return;

        if (!profile.onboarding_tour_completed) {
          setShowOnboardingTour(true);
          setPhase('tour');
          return;
        }

        await navigateHome(user);
      } catch (error) {
        console.error('Error resolving home workspace:', error);
        if (!cancelled) setPhase('error');
      }
    };

    resolveHome();
    return () => {
      cancelled = true;
    };
  }, [user, retryKey, searchParams, navigateHome]);

  const handleTourClose = async () => {
    setShowOnboardingTour(false);
    setPhase('loading');
    try {
      await navigateHome(user);
    } catch (error) {
      console.error('Error resolving home after tour close:', error);
      setPhase('error');
    }
  };

  const handleTryAgain = () => {
    setRetryKey((key) => key + 1);
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      dispatch(resetWorkspaceState());
      await persistor.flush();
      await persistor.purge();
      window.location.assign('/');
    } catch (error) {
      console.error('Error signing out from HomeGate:', error);
    }
  };

  if (phase === 'error') {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center p-6">
        <p className="text-neutral-900 font-medium mb-4">
          Could not load your workspace
        </p>
        <button
          type="button"
          onClick={handleTryAgain}
          className="px-5 py-2.5 bg-cranberry-700 text-white rounded-xl border-2 border-neutral-900 font-medium hover:bg-cranberry-600 transition-colors"
        >
          Try again
        </button>
        <button
          type="button"
          onClick={handleSignOut}
          className="mt-3 text-sm text-neutral-600 underline hover:text-neutral-900"
        >
          Sign out
        </button>
      </div>
    );
  }

  if (phase === 'tour' && user) {
    return (
      <OnboardingTour
        isOpen={showOnboardingTour}
        onClose={handleTourClose}
        userId={user.id}
      />
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center p-6">
      <div
        className="animate-spin rounded-full h-12 w-12 border-4 border-neutral-200 border-t-cranberry-600"
        aria-hidden="true"
      />
      <p className="mt-4 text-sm text-neutral-600">Setting up your workspace…</p>
    </div>
  );
};

export default HomeGate;
